// Global Instructions Rule Applied!

import { useCallback, useEffect, useRef, useState } from 'react'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { dismissErrorToast, showErrorToast } from '../../../core/utils/errorToast.tsx'
import { fetchInvestorStatementRun, generateRunStatements } from '../services/investorStatementsService.ts'
import type { GenerateTarget } from '../services/investorStatementsService.ts'
import { InvestorStatementRun } from '../../../state_manager/data_models/investor_statement_run.ts'

/** How often the run is re-read while statements are being generated. */
export const RUN_POLL_MS = 3000

/**
 * useInvestorStatementRun — one statement run for its form: the run, its
 * funds and the statements stored for them.
 *
 * While the server is generating (prog__is_active) the run is re-read every
 * few seconds until nothing is queued or pending - the generation runs in
 * the background on the server, so the run read is the only place its
 * progress shows. A failed poll raises one keyed error toast, taken down by
 * the next read that succeeds. (useCapitalCallNotices is the pattern.)
 *
 * Form-local, like a capital call's detail and its notices: not in a Redux
 * slice.
 */
export function useInvestorStatementRun(runId: string | null | undefined) {
  const [run, setRun] = useState<InvestorStatementRun | null>(null)
  const [loading, setLoading] = useState(!!runId)
  const [error, setError] = useState<string | null>(null)
  const [notFound, setNotFound] = useState(false)
  const [generating, setGenerating] = useState(false)
  // bumped by every poll, successful or not, so a failed one is retried
  const [pollTick, setPollTick] = useState(0)

  const pollToastKey = `investor-statement-run-poll-${runId ?? ''}`
  // a slow read must not overwrite a later one
  const requestSeq = useRef(0)

  const load = useCallback(
    async (background = false) => {
      if (!runId) {
        setRun(null)
        setError(null)
        setLoading(false)
        return
      }
      const seq = ++requestSeq.current
      if (!background) setLoading(true)
      const res = await fetchInvestorStatementRun(runId, getAccessToken(store.getState().auth))
      if (seq !== requestSeq.current) return
      if (res.data) {
        setRun(res.data)
        setError(null)
        setNotFound(false)
        dismissErrorToast(pollToastKey)
      } else if (background) {
        // keep showing what is there; say the refresh failed
        showErrorToast(res.error || 'Failed to refresh the statement run', { key: pollToastKey })
      } else {
        setError(res.error || 'Failed to load the statement run')
        setNotFound(!!res.notFound)
      }
      if (!background) setLoading(false)
    },
    [runId, pollToastKey]
  )

  useEffect(() => {
    void load()
  }, [load])

  const active = run?.prog__is_active ?? false
  useEffect(() => {
    if (!active) return
    const timer = setTimeout(() => {
      void load(true).then(() => setPollTick((tick) => tick + 1))
    }, RUN_POLL_MS)
    return () => clearTimeout(timer)
  }, [active, pollTick, load])

  useEffect(() => () => dismissErrorToast(pollToastKey), [pollToastKey])

  /** Queue a generation: every fund, the given funds, the given statements
   *  of those funds, or the given performance decks; every document group,
   *  or the groups named (reports, statements). */
  const generate = useCallback(
    async (target: GenerateTarget = {}): Promise<{ count: number | null; accountDocuments?: number; error?: string }> => {
      if (!runId) return { count: null, error: 'Create the run first' }
      setGenerating(true)
      try {
        const res = await generateRunStatements(runId, target, getAccessToken(store.getState().auth))
        await load(true)
        return res
      } finally {
        setGenerating(false)
      }
    },
    [runId, load]
  )

  const refetch = useCallback(() => load(true), [load])

  /** A write that returns the run (add funds) hands it straight in. */
  const replace = useCallback((next: InvestorStatementRun) => {
    requestSeq.current += 1
    setRun(next)
    setError(null)
  }, [])

  return { run, loading, error, notFound, refetch, replace, generate, generating }
}
